import { ClientEvents } from 'common/events/ClientEvents';
import React, { useEffect, useState } from 'react';
import { useRecordConsentDecisionMutation } from 'src/gqlReactTypings.generated.d';
import { Modal, ModalBody, ModalHeader } from 'src/shared/design-system/Modal';
import { useTracker } from 'src/shared/hooks/useTracker';
import { ConsentPreferences, useCcpaConsentStoreSelectors } from 'src/ztore/ccpa-consent-store';

export const CcpaCustomizeModal: React.FC = () => {
  const isCustomizeModalShowing = useCcpaConsentStoreSelectors.use.isCustomizeModalShowing();
  const consentPreferences = useCcpaConsentStoreSelectors.use.consentPreferences();
  const { saveCustomPreferences, acceptAll, hideCustomizeModal } = useCcpaConsentStoreSelectors.getState();
  const [recordConsent] = useRecordConsentDecisionMutation();
  const tracker = useTracker();
  const [advertisingEnabled, setAdvertisingEnabled] = useState(consentPreferences.advertising);

  // Sync local toggle state when modal opens
  useEffect(() => {
    if (isCustomizeModalShowing) {
      setAdvertisingEnabled(consentPreferences.advertising);
    }
  }, [isCustomizeModalShowing, consentPreferences.advertising]);

  if (!isCustomizeModalShowing) return null;

  const handleSave = () => {
    const prefs: ConsentPreferences = { advertising: advertisingEnabled };
    tracker.track(ClientEvents.CCPA_CUSTOM_PREFERENCES_SAVED, { advertising: advertisingEnabled });
    recordConsent({
      variables: { consentType: 'ADVERTISING', decision: advertisingEnabled ? 'ACCEPTED' : 'DECLINED' },
    }).catch(console.error);
    saveCustomPreferences(prefs);

    if (advertisingEnabled) {
      const success = tracker.enableAdTracking?.() ?? false;
      if (success) {
        tracker.track(ClientEvents.CCPA_AD_TRACKERS_INITIALIZED, { location: 'customize_modal' });
      }
    } else if (consentPreferences.advertising && !advertisingEnabled) {
      // Was enabled, now disabled — need reload to stop running trackers
      window.location.reload();
    }
  };

  const handleAcceptAll = () => {
    tracker.track(ClientEvents.CCPA_CONSENT_ACCEPTED, { location: 'customize_modal' });
    recordConsent({ variables: { consentType: 'ADVERTISING', decision: 'ACCEPTED' } }).catch(console.error);
    acceptAll();
    const success = tracker.enableAdTracking?.() ?? false;
    if (success) {
      tracker.track(ClientEvents.CCPA_AD_TRACKERS_INITIALIZED, { location: 'customize_modal' });
    }
  };

  return (
    <Modal isShowing={isCustomizeModalShowing} onClose={hideCustomizeModal} showCloseButton size='md'>
      <ModalHeader>Cookie Preferences</ModalHeader>
      <ModalBody>
        <div className='flex flex-col gap-6'>
          <p className='text-sm text-neutral-800'>
            Choose which types of cookies and tracking technologies you allow. Your preferences are saved on this
            browser.
          </p>

          {/* Strictly Necessary — always on */}
          <div className='flex items-center justify-between rounded-lg border border-neutral-200 p-4'>
            <div className='flex-1 pr-4'>
              <p id='necessary-toggle-label' className='text-sm font-semibold text-eggplant-600'>
                Strictly Necessary
              </p>
              <p className='mt-1 text-xs text-neutral-600'>
                Essential cookies, error monitoring, and analytics required for site operation. These cannot be
                disabled.
              </p>
            </div>
            <div
              className='relative inline-flex cursor-not-allowed'
              role='switch'
              aria-checked
              aria-labelledby='necessary-toggle-label'
              aria-disabled>
              <div className='h-6 w-11 rounded-full bg-kale-400 opacity-60'>
                <div className='absolute left-[22px] top-0.5 size-5 rounded-full bg-white shadow' />
              </div>
            </div>
          </div>

          {/* Targeting & Advertising — toggleable */}
          <div className='flex items-center justify-between rounded-lg border border-neutral-200 p-4'>
            <div className='flex-1 pr-4'>
              <p id='ad-toggle-label' className='text-sm font-semibold text-eggplant-600'>
                Targeting & Advertising
              </p>
              <p className='mt-1 text-xs text-neutral-600'>
                Personalized ads from Facebook, Google, LinkedIn, and other ad networks. Used for cross-site tracking
                and retargeting.
              </p>
            </div>
            <button
              type='button'
              onClick={() => setAdvertisingEnabled(!advertisingEnabled)}
              className='relative inline-flex shrink-0'
              role='switch'
              aria-checked={advertisingEnabled}
              aria-labelledby='ad-toggle-label'>
              <div
                className={`h-6 w-11 rounded-full transition-colors ${
                  advertisingEnabled ? 'bg-kale-400' : 'bg-neutral-300'
                }`}>
                <div
                  className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-transform ${
                    advertisingEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'
                  }`}
                />
              </div>
            </button>
          </div>

          <div className='flex gap-3'>
            <button
              type='button'
              onClick={handleSave}
              className='hover:bg-eggplant-700 rounded-lg bg-eggplant-600 px-6 py-3 text-sm font-semibold text-white'>
              Save Preferences
            </button>
            <button
              type='button'
              onClick={handleAcceptAll}
              className='rounded-lg border border-eggplant-300 px-6 py-3 text-sm font-semibold text-eggplant-600 hover:bg-eggplant-50'>
              Accept All
            </button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};
